import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Button,
  Grow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Link,
  Tooltip,
  Snackbar,
  capitalize,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  FileDownload as FileDownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Pending as PendingIcon,
  Send as SendIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Gavel as GavelIcon,
  Info as InfoIcon,
  Replay as ReplayIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import AdvancedPagination from '@/components/pagination/AdvancedPagination'
import { useDebounce } from '@/hooks/useDebounce'
import ClaimDialog from '@/components/contracts/ClaimDialog'
import InvoiceDialog from '@/components/contracts/InvoiceDialog'
import api from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import {
  getClaims,
  exportClaimsCSV,
  getClaimsSummary,
  getContractDropdowns,
  Claim,
  ClaimParams,
  ClaimStatus,
  ContractDropdownOption,
  ClaimsSummaryResponse,
} from '@/services/claimsService'
import { ExternalLink } from 'lucide-react'
import {
  useAgencyDirectory,
  getDirectoryOptionLabel,
} from '@/hooks/useAgencyDirectory'

const AgencyClaimPage: React.FC = () => {
  const { isPatient } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [claims, setClaims] = useState<Claim[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [totalAmount, setTotalAmount] = useState<number>(0)

  // Summary data
  const [summaryData, setSummaryData] = useState<ClaimsSummaryResponse>({
    pending: 0,
    submitted: 0,
    accepted: 0,
    paid: 0,
    rejected: 0,
  })

  // Filters - Initialize search from location state if provided
  const [search, setSearch] = useState<string>(
    (location.state as any)?.searchQuery || ''
  )
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [contractId, setContractId] = useState<string>('')
  const [status, setStatus] = useState<ClaimStatus | ''>('')
  const [filterPatientId, setFilterPatientId] = useState<string>('')
  const [filterProviderId, setFilterProviderId] = useState<string>('')

  const { patients, providers } = useAgencyDirectory()

  // Dropdown options
  const [contractOptions, setContractOptions] = useState<
    ContractDropdownOption[]
  >([])
  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [selectedContract, setSelectedContract] =
    useState<ContractDropdownOption | null>(null)
  const [contractSearch, setContractSearch] = useState<string>('')

  // Pagination
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)

  // Claim Dialog state
  const [claimDialogOpen, setClaimDialogOpen] = useState(false)
  const [claimDialogData, setClaimDialogData] = useState<Claim | null>(null)
  const [loadingClaim, setLoadingClaim] = useState(false)
  const [claimDialogReadOnly, setClaimDialogReadOnly] = useState(false)
  const [isResubmit, setIsResubmit] = useState(false)
  // Invoice Dialog state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [invoiceDialogData, setInvoiceDialogData] = useState<any>(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [invoiceDialogReadOnly, setInvoiceDialogReadOnly] = useState(false)

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success')

  const debouncedSearch = useDebounce(search, 500)

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoadingDropdowns(true)
      try {
        const contracts = await getContractDropdowns()
        setContractOptions(contracts)
      } catch (err) {
        console.error('Error fetching dropdowns:', err)
      } finally {
        setLoadingDropdowns(false)
      }
    }
    fetchDropdowns()
  }, [])

  // Fetch contracts when search changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const fetchContracts = async () => {
        setLoadingDropdowns(true)
        try {
          const contracts = await getContractDropdowns(contractSearch)
          setContractOptions(contracts)
        } catch (err) {
          console.error('Error fetching contracts:', err)
        } finally {
          setLoadingDropdowns(false)
        }
      }
      if (contractSearch) {
        fetchContracts()
      } else {
        // Reset to initial list when search is cleared
        const fetchDropdowns = async () => {
          setLoadingDropdowns(true)
          try {
            const contracts = await getContractDropdowns()
            setContractOptions(contracts)
          } catch (err) {
            console.error('Error fetching dropdowns:', err)
          } finally {
            setLoadingDropdowns(false)
          }
        }
        fetchDropdowns()
      }
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [contractSearch])

  // Fetch summary data
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const summary = await getClaimsSummary()
        setSummaryData({
          pending: summary.pending,
          submitted: summary.submitted,
          accepted: summary.accepted,
          paid: summary.paid,
          rejected: summary.rejected,
        })
      } catch (err) {
        console.error('Error fetching summary:', err)
        setSummaryData({
          pending: 0,
          submitted: 0,
          accepted: 0,
          paid: 0,
          rejected: 0,
        })
      }
    }
    fetchSummary()
  }, [])

  const fetchClaims = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Convert dates to YYYY-MM-DD format for API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined
        return format(date, 'yyyy-MM-dd')
      }

      const params: ClaimParams = {
        search: debouncedSearch || undefined,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        contractId: contractId || undefined,
        status: status || undefined,
        patientId: filterPatientId || undefined,
        therapistId: filterProviderId || undefined,
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      }

      const response = await getClaims(params)
      const claimsList = response.results || response.data || []
      setClaims(claimsList)
      setTotalRecords(
        response.total || response.totalRecords || claimsList.length
      )
      setTotalAmount(response.totalAmount || 0)
    } catch (err: any) {
      console.error('Error fetching claims:', err)
      setError(err?.response?.data?.message || 'Failed to fetch claims')
      setClaims([])
      setTotalRecords(0)
      setTotalAmount(0)
    } finally {
      setLoading(false)
    }
  }, [
    debouncedSearch,
    startDate,
    endDate,
    contractId,
    status,
    filterPatientId,
    filterProviderId,
    page,
    rowsPerPage,
  ])

  useEffect(() => {
    fetchClaims()
  }, [fetchClaims])

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleExportCSV = async () => {
    try {
      setLoading(true)

      // Convert dates to YYYY-MM-DD format for API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined
        return format(date, 'yyyy-MM-dd')
      }

      const params: Omit<ClaimParams, 'page' | 'limit'> = {
        search: debouncedSearch || undefined,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        contractId: contractId || undefined,
        status: status || undefined,
        patientId: filterPatientId || undefined,
        therapistId: filterProviderId || undefined,
      }

      const blob = await exportClaimsCSV(params)

      // Create download link
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `claims-${format(new Date(), 'yyyy-MM-dd')}.csv`
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Error exporting claims:', err)
      setError(err?.response?.data?.message || 'Failed to export claims')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setSearch('')
    setStartDate(null)
    setEndDate(null)
    setContractId('')
    setStatus('')
    setSelectedContract(null)
    setContractSearch('')
    setFilterPatientId('')
    setFilterProviderId('')
    setPage(0)
  }

  const hasActiveFilters = Boolean(
    search ||
    startDate ||
    endDate ||
    contractId ||
    status ||
    selectedContract ||
    filterPatientId ||
    filterProviderId
  )

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const getStatusConfig = (status: ClaimStatus) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return {
          color: 'warning',
          label: 'Pending',
          icon: <PendingIcon fontSize="small" />,
        }
      case 'submitted':
        return {
          color: '#17a2b8',
          label: 'Submitted',
          icon: <SendIcon fontSize="small" />,
        }
      case 'accepted':
        return {
          color: 'info',
          label: 'Accepted',
          icon: <CheckCircleIcon fontSize="small" />,
        }
      case 'rejected':
        return {
          color: 'error',
          label: 'Rejected',
          icon: <CancelIcon fontSize="small" />,
        }
      case 'paid':
        return {
          color: 'success',
          label: 'Paid',
          icon: <PaymentIcon fontSize="small" />,
        }
      default:
        return { color: '#6c757d', label: capitalize(status), icon: null }
    }
  }

  const getStatusColor = (status: ClaimStatus) => {
    switch (status) {
      case 'submitted':
        return '#17a2b8' // teal/cyan
      case 'pending':
        return '#ffc107' // yellow
      case 'accepted':
        return '#007bff' // blue
      case 'rejected':
        return '#dc3545' // red
      case 'paid':
        return '#28a745' // green
      default:
        return '#6c757d' // gray
    }
  }

  const getContractId = (claim: Claim): string => {
    // New format: contractId is an object with contract_ids property
    if (
      typeof claim.contractId === 'object' &&
      claim.contractId?.contract_ids
    ) {
      return claim.contractId.contract_ids
    }
    // Fallback to old format
    if (
      typeof claim.contract_id === 'object' &&
      claim.contract_id?.contract_ids
    ) {
      return claim.contract_id.contract_ids
    }
    if (typeof claim.contract_id === 'string') {
      return claim.contract_id
    }
    if (claim.contract_ids) {
      return claim.contract_ids
    }
    return 'N/A'
  }

  const getClaimId = (claim: Claim): string => {
    return claim.claimId || claim.claim_id || claim._id || 'N/A'
  }

  const getSessionId = (claim: Claim): string => {
    // New format: sessionIds is a string at top level
    if (claim.sessionIds) {
      return claim.sessionIds
    }
    // New format: sessionId is an object with session_ids property
    if (
      claim.sessionId &&
      typeof claim.sessionId === 'object' &&
      !Array.isArray(claim.sessionId) &&
      claim.sessionId.session_ids
    ) {
      return claim.sessionId.session_ids
    }
    // Fallback to old format
    return claim.session_ids || claim.session_id || 'N/A'
  }

  const getPatientName = (claim: Claim): string => {
    // New format: patientFirstName and patientLastName are separate fields
    if (claim.patientFirstName && claim.patientLastName) {
      return `${claim.patientFirstName} ${claim.patientLastName}`
    }
    // Fallback to old format
    return claim.patient_name || claim.patientName || 'N/A'
  }

  const getProviderName = (claim: Claim): string => {
    // Check for provider_name field
    if (claim.provider_name) {
      return claim.provider_name
    }

    if (claim as any) {
      return `${(claim as any).rProvFistName || ''} ${(claim as any).rProvLastName || ''}`
    }

    // Check if therapistId is an object with firstName and lastName
    if (
      claim.therapistId &&
      typeof claim.therapistId === 'object' &&
      claim.therapistId.firstName &&
      claim.therapistId.lastName
    ) {
      return `${claim.therapistId.firstName} ${claim.therapistId.lastName}`
    }
    return 'N/A'
  }

  const getPayer = (claim: Claim): string => {
    // New format: payerName
    return claim.payerName || claim.payer || claim.payer_name || 'N/A'
  }

  const getClaimedAmount = (claim: Claim): number => {
    // New format: claimedAmount (camelCase)
    return claim.claimedAmount || claim.claimed_amount || 0
  }

  const getApprovedAmount = (claim: Claim): number | string => {
    // Check sessionId object first (new format)
    if (
      claim.sessionId &&
      typeof claim.sessionId === 'object' &&
      !Array.isArray(claim.sessionId) &&
      claim.sessionId.approved_amount !== undefined
    ) {
      return claim.sessionId.approved_amount
    }
    // Top level fields
    return claim.approvedAmount ?? claim.approved_amount ?? 'NA'
  }

  const getCoPay = (claim: Claim): number | string => {
    // New format: coPayAmount (camelCase)
    if (claim.coPayAmount !== undefined) {
      return claim.coPayAmount
    }
    // Check sessionId object (new format)
    if (
      claim.sessionId &&
      typeof claim.sessionId === 'object' &&
      !Array.isArray(claim.sessionId) &&
      claim.sessionId.co_pay !== undefined
    ) {
      return claim.sessionId.co_pay
    }
    // Fallback to old format
    return claim.co_pay ?? claim.coPay ?? 'NA'
  }

  const getClaimStatus = (claim: Claim): ClaimStatus => {
    return claim.status || claim.claim_status || 'submitted'
  }

  // Helper to extract session ID from claim
  const getSessionIdForApi = (claim: Claim): string => {
    // If sessionId is an object, extract _id
    if (
      claim.sessionId &&
      typeof claim.sessionId === 'object' &&
      !Array.isArray(claim.sessionId)
    ) {
      return (claim.sessionId as any)._id || ''
    }
    // Otherwise use string values
    if (typeof claim.sessionId === 'string') {
      return claim.sessionId
    }
    return claim.session_id || claim.sessionIds || ''
  }

  const handleView = async (claimId: string) => {
    setLoadingClaim(true)
    setClaimDialogReadOnly(true)
    setIsResubmit(false)
    setClaimDialogOpen(true)

    try {
      const claim = claims.find(c => c._id === claimId)
      if (claim) {
        const sessionId = getSessionIdForApi(claim)
        if (sessionId) {
          // Fetch full claim details
          const response = await api.get(
            `/insurance-claims/claim-detail/${sessionId}`
          )
          if (response.data) {
            setClaimDialogData(response.data)
          } else {
            setClaimDialogData(claim as any)
          }
        } else {
          setClaimDialogData(claim as any)
        }
      }
    } catch (err: any) {
      console.error('Error fetching claim details:', err)
      setSnackbarMessage('Failed to load claim details. Please try again.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setClaimDialogOpen(false)
    } finally {
      setLoadingClaim(false)
    }
  }

  const handleEdit = async (claimId: string) => {
    setLoadingClaim(true)
    setClaimDialogReadOnly(false)
    setIsResubmit(false)
    setClaimDialogOpen(true)

    try {
      const claim = claims.find(c => c._id === claimId)
      if (claim) {
        const sessionId = getSessionIdForApi(claim)
        if (sessionId) {
          // Fetch full claim details
          const response = await api.get(
            `/insurance-claims/claim-detail/${sessionId}`
          )
          if (response.data) {
            setClaimDialogData(response.data)
          } else {
            setClaimDialogData(claim as any)
          }
        } else {
          setClaimDialogData(claim as any)
        }
      }
    } catch (err: any) {
      console.error('Error fetching claim details:', err)
      setSnackbarMessage('Failed to load claim details. Please try again.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setClaimDialogOpen(false)
    } finally {
      setLoadingClaim(false)
    }
  }

  const handleResubmit = async (claimId: string) => {
    setLoadingClaim(true)
    setClaimDialogReadOnly(false)
    setIsResubmit(true)
    setClaimDialogOpen(true)

    try {
      const claim = claims.find(c => c._id === claimId)
      if (claim) {
        const sessionId = getSessionIdForApi(claim)
        if (sessionId) {
          // Fetch full claim details
          const response = await api.get(
            `/insurance-claims/claim-detail/${sessionId}`
          )
          if (response.data) {
            setClaimDialogData(response.data)
          } else {
            setClaimDialogData(claim as any)
          }
        } else {
          setClaimDialogData(claim as any)
        }
      }
    } catch (err: any) {
      console.error('Error fetching claim details:', err)
      setSnackbarMessage('Failed to load claim details. Please try again.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setClaimDialogOpen(false)
    } finally {
      setLoadingClaim(false)
    }
  }

  const handleDownload = (claim: Claim) => {
    if (claim.documentUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = claim.documentUrl
      link.download = `claim-${claim.claimId || claim.claim_id || claim._id}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      setSnackbarMessage('No document available for download.')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
    }
  }

  const handleGenerateClaim = async (payload: any) => {
    try {
      const endpoint = isResubmit
        ? '/insurance-claims/resubmit-full-claim'
        : '/insurance-claims/update-full-claim'

      const response = await api.post(endpoint, payload)

      if (response.data) {
        // Show message from API response if available, otherwise use default message
        const successMessage =
          response.data.message ||
          (isResubmit
            ? 'Claim resubmitted successfully!'
            : 'Claim updated successfully!')

        setSnackbarMessage(successMessage)
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
        setClaimDialogOpen(false)
        // Refresh claims list
        fetchClaims()
      }
    } catch (err: any) {
      console.error('Error submitting claim:', err)
      const errorMsg = err.response?.data?.message
      setSnackbarMessage(
        typeof errorMsg === 'string'
          ? errorMsg
          : errorMsg?.message ||
          errorMsg?.mesgid ||
          (isResubmit
            ? 'Failed to resubmit claim. Please try again.'
            : 'Failed to update claim. Please try again.')
      )
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const handleRaiseInvoice = async (claimId: string) => {
    setLoadingInvoice(true)
    setInvoiceDialogReadOnly(false)
    setInvoiceDialogOpen(true)

    try {
      // Fetch invoice data from the API
      const response = await api.get(
        `/insurance-claims/${claimId}/raise-invoice`
      )
      if (response.data) {
        // API returns { message, invoice: {...} }, so extract the invoice object
        const invoiceData = response.data.invoice || response.data
        setInvoiceDialogData(invoiceData)
      } else {
        setSnackbarMessage('Failed to load invoice details. Please try again.')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        setInvoiceDialogOpen(false)
      }
    } catch (err: any) {
      console.error('Error fetching invoice details:', err)
      setSnackbarMessage(
        err.response?.data?.message ||
        'Failed to load invoice details. Please try again.'
      )
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setInvoiceDialogOpen(false)
    } finally {
      setLoadingInvoice(false)
    }
  }

  // Commented out - Now redirecting to invoices page with search query instead of opening dialog
  // const handleViewInvoice = async (claim: Claim) => {
  //   setLoadingInvoice(true)
  //   setInvoiceDialogReadOnly(true)
  //   setInvoiceDialogOpen(true)

  //   try {
  //     // Extract invoice ID from claim
  //     const invoiceId =
  //       typeof claim.invoiceId === 'object'
  //         ? (claim.invoiceId as any)?._id ||
  //           (claim.invoiceId as any)?.invoice_ids ||
  //           ''
  //         : claim.invoiceId || claim.invoice_id || ''

  //     if (invoiceId) {
  //       // Fetch invoice details using invoice detail API
  //       const response = await api.get(`/invoices/${invoiceId}`)
  //       if (response.data) {
  //         setInvoiceDialogData(response.data)
  //       } else {
  //         setSnackbarMessage(
  //           'Failed to load invoice details. Please try again.'
  //         )
  //         setSnackbarSeverity('error')
  //         setSnackbarOpen(true)
  //         setInvoiceDialogOpen(false)
  //       }
  //     } else {
  //       setSnackbarMessage('Invoice ID not found.')
  //       setSnackbarSeverity('warning')
  //       setSnackbarOpen(true)
  //       setInvoiceDialogOpen(false)
  //     }
  //   } catch (err: any) {
  //     console.error('Error fetching invoice details:', err)
  //     setSnackbarMessage(
  //       err.response?.data?.message ||
  //         'Failed to load invoice details. Please try again.'
  //     )
  //     setSnackbarSeverity('error')
  //     setSnackbarOpen(true)
  //     setInvoiceDialogOpen(false)
  //   } finally {
  //     setLoadingInvoice(false)
  //   }
  // }

  const handleDownloadInvoice = (claim: Claim) => {
    if (claim.invoiceUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = claim.invoiceUrl
      link.download = `invoice-${claim.invoiceId || claim._id}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      setSnackbarMessage('No invoice document available for download.')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
    }
  }

  const handleGenerateInvoice = async (payload: {
    session_id: string
    bill_address: string
    bill_city: string
    bill_state: string
    bill_zip: string
    discount: number
    amount: number
  }) => {
    try {
      // Use the existing invoice generation API
      const response = await api.post('/invoices/generate', payload)

      if (response.data) {
        setSnackbarMessage('Invoice generated successfully!')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
        setInvoiceDialogOpen(false)
        // Refresh claims list
        fetchClaims()
      }
    } catch (err: any) {
      console.error('Error generating invoice:', err)
      setSnackbarMessage(
        err.response?.data?.message ||
        'Failed to generate invoice. Please try again.'
      )
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  // Helper to get invoice ID as string
  const getInvoiceId = (claim: Claim): string => {
    if (typeof claim.invoiceId === 'object' && claim.invoiceId !== null) {
      return (
        (claim.invoiceId as any).invoice_ids ||
        (claim.invoiceId as any)._id ||
        ''
      )
    }
    return claim.invoiceId || claim.invoice_id || ''
  }

  // Helper to check if invoice is raised
  const isInvoiceRaised = (claim: Claim): boolean => {
    return (
      claim.invoiceRaised === true ||
      claim.invoice_raised === true ||
      !!getInvoiceId(claim)
    )
  }

  // Helper to get coPay amount
  const getCoPayAmount = (claim: Claim): number => {
    const coPay = getCoPay(claim)
    return typeof coPay === 'number' ? coPay : 0
  }

  const statusOptions: ClaimStatus[] = [
    'pending',
    'submitted',
    'accepted',
    'paid',
    'rejected',
  ]

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: 'primary.main', fontWeight: 600 }}
            gutterBottom
          >
            Insurance Claims
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track your insurance claims
          </Typography>
        </Box>
        {!isPatient && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading || claims.length === 0}
              sx={{
                textTransform: 'none',
                borderRadius: '0.5rem',
                fontWeight: 500,
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Export CSV
            </Button>
          </Box>
        )}
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        {Object.entries(summaryData).map(([status, count]) => {
          const statusColor = getStatusColor(status as ClaimStatus)
          return (
            <Card
              key={status}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '1rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    }}
                  >
                    {count}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: statusColor,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                      }}
                    >
                      {capitalize(status)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )
        })}
      </Box>

      {/* Filters Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '1rem',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
              {/* Main Filters Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'flex-end',
                }}
              >
                {/* Search Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(40% - 8px)',
                      lg: '2 1 200px',
                    },
                    minWidth: { xs: '100%', sm: '200px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Search
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search by Invoice ID, Service, Session ID or Patient..."
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary', fontSize: '18px' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.625rem',
                        backgroundColor: 'grey.50',
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'grey.100',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'background.paper',
                        },
                        '& fieldset': {
                          borderColor: 'grey.300',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '1.5px',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Date Filters Group */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 300px',
                    },
                    minWidth: { xs: '100%', sm: '300px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Start date & End date
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <DatePicker
                      value={startDate}
                      onChange={(newValue: Date | null) =>
                        setStartDate(newValue)
                      }
                      format="MM/dd/yyyy"
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'Start Date',
                          sx: {
                            flex: 1,
                            minWidth: '150px',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0.625rem',
                              backgroundColor: 'grey.50',
                              fontSize: '0.875rem',
                              '&:hover': {
                                backgroundColor: 'grey.100',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'background.paper',
                              },
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1.5px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              minWidth: '150px',
                            },
                          },
                        },
                      }}
                    />
                    <DatePicker
                      value={endDate}
                      onChange={(newValue: Date | null) => setEndDate(newValue)}
                      format="MM/dd/yyyy"
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'End Date',
                          sx: {
                            flex: 1,
                            minWidth: '150px',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0.625rem',
                              backgroundColor: 'grey.50',
                              fontSize: '0.875rem',
                              '&:hover': {
                                backgroundColor: 'grey.100',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'background.paper',
                              },
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1.5px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              minWidth: '150px',
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Contract ID Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Contract Id
                  </Typography>
                  <Autocomplete
                    options={contractOptions}
                    getOptionLabel={option => option.contract_ids || ''}
                    value={selectedContract}
                    onChange={(event, newValue) => {
                      setSelectedContract(newValue)
                      setContractId(newValue?._id || '')
                    }}
                    onInputChange={(event, newInputValue) => {
                      setContractSearch(newInputValue)
                    }}
                    loading={loadingDropdowns}
                    size="small"
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Select Contract ID"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                <PersonIcon
                                  fontSize="small"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '18px',
                                  }}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                        }}
                      />
                    )}
                    slotProps={{
                      popper: { sx: { zIndex: 9999 } },
                    }}
                  />
                </Box>

                {/* Claim Status Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Claim Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={status}
                      onChange={e =>
                        setStatus(e.target.value as ClaimStatus | '')
                      }
                      displayEmpty
                      sx={{
                        borderRadius: '0.625rem',
                        backgroundColor: 'grey.50',
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'grey.100',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'background.paper',
                        },
                        '& fieldset': {
                          borderColor: 'grey.300',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '1.5px',
                        },
                      }}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      {statusOptions.map(option => (
                        <MenuItem key={option} value={option}>
                          {capitalize(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Patient Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Patient
                  </Typography>
                  <Select
                    value={filterPatientId}
                    onChange={event => setFilterPatientId(event.target.value as string)}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    }}
                  >
                    <MenuItem value="">All Patients</MenuItem>
                    {patients.map(patient => (
                      <MenuItem key={patient._id} value={patient._id}>
                        {getDirectoryOptionLabel(patient)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Provider Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Provider
                  </Typography>
                  <Select
                    value={filterProviderId}
                    onChange={event => setFilterProviderId(event.target.value as string)}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    }}
                  >
                    <MenuItem value="">All Providers</MenuItem>
                    {providers.map(provider => (
                      <MenuItem key={provider._id} value={provider._id}>
                        {getDirectoryOptionLabel(provider)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Reset Filter Button */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '0 0 auto',
                    },
                    minWidth: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    alignItems: 'flex-end',
                  }}
                >
                  <IconButton
                    onClick={handleClearFilters}
                    size="small"
                    sx={{
                      borderRadius: '0.625rem',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      width: '40px',
                      height: '40px',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <SyncIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {claims.length} of {totalRecords} claims
          {totalAmount > 0 && (
            <span style={{ marginLeft: '8px', fontWeight: 900 }}>
              • Total Amount: ${totalAmount.toFixed(2)}
            </span>
          )}
        </Typography>
      </Box>

      {/* Table Card */}
      <Grow in timeout={300}>
        <Card sx={{ boxShadow: 2 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 400,
                }}
              >
                <CircularProgress />
              </Box>
            ) : claims.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 400,
                  gap: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No claims found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {hasActiveFilters
                    ? 'Try adjusting or clearing your filters to see more results.'
                    : 'Claims will appear here once they are created.'}
                </Typography>
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Claim ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Remote Claim ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Contract ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Session ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Payer
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Submission Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        {isPatient ? 'Provider Name' : 'Patient Name'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Claimed Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Paid Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Co Pay
                      </TableCell>
                      {!isPatient && (
                        <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                          Platform Fee
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Claim Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Prior Auth Number
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {claims.map((claim, index) => {
                      const claimStatus = getClaimStatus(claim)
                      const statusConfig = getStatusConfig(claimStatus)
                      const contractIdValue = getContractId(claim)
                      const contractMongoId =
                        (claim.contractId as any)?._id || ''
                      const coPay = getCoPay(claim)
                      const approvedAmount = getApprovedAmount(claim)

                      return (
                        <Grow
                          in
                          key={claim._id}
                          timeout={200 + index * 50}
                          style={{ transformOrigin: '0 0 0' }}
                        >
                          <TableRow
                            hover
                            sx={{
                              cursor: 'default',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {getClaimId(claim)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {claim?.remote_claimid || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Link
                                component={RouterLink}
                                to={
                                  isPatient
                                    ? `/contract-details/${contractMongoId}`
                                    : `/therapy-contracts/${contractMongoId}`
                                }
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: 'primary.dark',
                                  },
                                }}
                              >
                                {contractIdValue}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {getSessionId(claim)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {getPayer(claim)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(
                                  claim.submission_date || claim.submissionDate
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {isPatient
                                  ? getProviderName(claim)
                                  : getPatientName(claim)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                ${getClaimedAmount(claim)}

                                {claim.status.toLowerCase() === 'rejected' && (
                                  <>
                                    {/* Invoice Already Raised */}
                                    {isInvoiceRaised(claim) && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          mt: 0.5,
                                        }}
                                      >
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={() =>
                                            navigate('/invoices', {
                                              state: {
                                                searchQuery: getInvoiceId(claim),
                                              },
                                            })
                                          }
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            textDecoration: 'underline',
                                            textAlign: 'start',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            '&:hover': {
                                              color: 'primary.dark',
                                            },
                                          }}
                                        >
                                          Invoice ID: {getInvoiceId(claim)}
                                          <ExternalLink
                                            className="w-3 h-3"
                                            style={{ color: 'primary.main' }}
                                          />
                                        </Link>

                                        {claim.invoiceUrl && (
                                          <Tooltip placement="top" title="Download Invoice">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDownloadInvoice(claim)}
                                              sx={{
                                                color: 'primary.main',
                                                p: 0.5,
                                                '&:hover': {
                                                  backgroundColor: 'action.hover',
                                                },
                                              }}
                                            >
                                              <FileDownloadIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                ${claim.amount_paid.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {typeof coPay === 'number'
                                  ? `$${coPay.toFixed(2)}`
                                  : coPay}
                              </Typography>
                              {claim.status.toLowerCase() !== 'rejected' && (
                                <>
                                  {isInvoiceRaised(claim) && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: 0.5,
                                      }}
                                    >
                                      <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() =>
                                          navigate('/invoices', {
                                            state: {
                                              searchQuery: getInvoiceId(claim),
                                            },
                                          })
                                        }
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          textDecoration: 'underline',
                                          textAlign: 'start',
                                          whiteSpace: 'nowrap',
                                          cursor: 'pointer',
                                          color: 'primary.main',
                                          '&:hover': {
                                            color: 'primary.dark',
                                          },
                                        }}
                                      >
                                        Invoice ID: {getInvoiceId(claim)}
                                        <ExternalLink
                                          className="w-3 h-3"
                                          style={{ color: 'primary.main' }}
                                        />
                                      </Link>
                                      {claim.invoiceUrl && (
                                        <Tooltip placement="top" title="Download Invoice">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDownloadInvoice(claim)
                                            }
                                            sx={{
                                              color: 'primary.main',
                                              p: 0.5,
                                              '&:hover': {
                                                backgroundColor: 'action.hover',
                                              },
                                            }}
                                          >
                                            <FileDownloadIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  )}
                                </>
                              )}
                            </TableCell>
                            {!isPatient && (
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  $
                                  {claim.commissionAmount?.toFixed(2) || '0.00'}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {(() => {
                                  const isCustomColor =
                                    typeof statusConfig.color === 'string' &&
                                    statusConfig.color.startsWith('#')
                                  return (
                                    <Chip
                                      icon={statusConfig.icon || undefined}
                                      label={statusConfig.label}
                                      color={
                                        isCustomColor
                                          ? undefined
                                          : (statusConfig.color as any)
                                      }
                                      size="small"
                                      sx={{
                                        fontWeight: 500,
                                        borderRadius: '1rem',
                                        ...(isCustomColor
                                          ? {
                                            backgroundColor:
                                              statusConfig.color,
                                            color: 'white',
                                            '& .MuiChip-icon': {
                                              color: 'white',
                                            },
                                          }
                                          : {}),
                                      }}
                                    />
                                  )
                                })()}
                                {/* Show info icon only for rejected and paid status */}
                                {(claimStatus.toLowerCase() === 'rejected' ||
                                  claimStatus.toLowerCase() === 'paid') &&
                                  (claimStatus.toLowerCase() === 'rejected' &&
                                    claim.claimMdMessages &&
                                    Array.isArray(claim.claimMdMessages) &&
                                    claim.claimMdMessages.length > 0 ? (
                                    <Tooltip placement="top" title={
                                      <Box sx={{ p: 1, maxWidth: 400 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: 'white',
                                          }}
                                        >
                                          Claim Messages:
                                        </Typography>
                                        <List
                                          dense
                                          sx={{
                                            p: 0,
                                            m: 0,
                                            listStyle: 'disc',
                                            pl: 2,
                                          }}
                                        >
                                          {claim.claimMdMessages.map(
                                            (
                                              message: string,
                                              idx: number
                                            ) => (
                                              <ListItem
                                                key={idx}
                                                sx={{
                                                  p: 0,
                                                  py: 0.3,
                                                  alignItems: 'flex-start',
                                                  display: 'list-item',
                                                  listStyleType: 'disc',
                                                }}
                                              >
                                                <ListItemText
                                                  primary={
                                                    typeof message === 'string'
                                                      ? message
                                                      : (message as any)?.message ||
                                                      (message as any)?.mesgid ||
                                                      'Unknown claim message'
                                                  }
                                                  primaryTypographyProps={{
                                                    variant: 'body2',
                                                    sx: {
                                                      color: 'white',
                                                      wordBreak: 'break-word',
                                                    },
                                                  }}
                                                />
                                              </ListItem>
                                            )
                                          )}
                                        </List>
                                      </Box>
                                    }
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                                            color: 'white',
                                            maxWidth: 450,
                                            '& .MuiTooltip-arrow': {
                                              color: 'rgba(0, 0, 0, 0.9)',
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <IconButton
                                        size="small"
                                        sx={{
                                          color: 'white',
                                          backgroundColor: 'info.light',
                                          '&:hover': {
                                            backgroundColor: 'info.main',
                                            color: 'white',
                                          },
                                        }}
                                      >
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : claimStatus.toLowerCase() === 'paid' ? (
                                    <Tooltip placement="top" title={
                                      <Box sx={{ p: 1, maxWidth: 400 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: 'white',
                                          }}
                                        >
                                          Payment Information:
                                        </Typography>
                                        <List dense sx={{ p: 0, m: 0 }}>
                                          <ListItem sx={{ p: 0, py: 0.3 }}>
                                            <ListItemText
                                              primary={`Paid Date: ${claim.paymentDate ? formatDate(claim.paymentDate) : 'N/A'}`}
                                              primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: {
                                                  color: 'white',
                                                },
                                              }}
                                            />
                                          </ListItem>
                                          <ListItem sx={{ p: 0, py: 0.3 }}>
                                            <ListItemText
                                              primary={`ClaimMD Provider Name: ${claim.claimmd_prov_name || 'N/A'}`}
                                              primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: {
                                                  color: 'white',
                                                },
                                              }}
                                            />
                                          </ListItem>
                                          <ListItem sx={{ p: 0, py: 0.3 }}>
                                            <ListItemText
                                              primary={`Check Number: ${claim.check_number || 'N/A'}`}
                                              primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: {
                                                  color: 'white',
                                                },
                                              }}
                                            />
                                          </ListItem>
                                        </List>
                                      </Box>
                                    }
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                                            color: 'white',
                                            maxWidth: 450,
                                            '& .MuiTooltip-arrow': {
                                              color: 'rgba(0, 0, 0, 0.9)',
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <IconButton
                                        size="small"
                                        sx={{
                                          color: 'white',
                                          backgroundColor: 'info.light',
                                          '&:hover': {
                                            backgroundColor: 'info.main',
                                            color: 'white',
                                          },
                                        }}
                                      >
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null)}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {claim.prior_auth_number || 'N/A'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </Grow>
                      )
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalRecords > 0 && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}
                  >
                    <AdvancedPagination
                      count={totalRecords}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handlePageChange}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </Box>
                )}
              </>
            )}
          </TableContainer>
        </Card>
      </Grow>

      {/* Claim Dialog */}
      <ClaimDialog
        open={claimDialogOpen}
        onClose={() => {
          setClaimDialogOpen(false)
          setClaimDialogData(null)
          setClaimDialogReadOnly(false)
          setIsResubmit(false)
        }}
        claimData={claimDialogData as any}
        loading={loadingClaim}
        readOnly={claimDialogReadOnly}
        isResubmit={isResubmit}
        onGenerateClaim={claimDialogReadOnly ? undefined : handleGenerateClaim}
      />

      {/* Invoice Dialog */}
      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => {
          setInvoiceDialogOpen(false)
          setInvoiceDialogData(null)
          setInvoiceDialogReadOnly(false)
        }}
        invoiceData={invoiceDialogData}
        loading={loadingInvoice}
        readOnly={invoiceDialogReadOnly}
        onGenerateInvoice={
          invoiceDialogReadOnly ? undefined : handleGenerateInvoice
        }
      />

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AgencyClaimPage
